import React, { FC, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { InputAdornment, IconButton, makeStyles, SvgIcon, Theme, alpha } from '@material-ui/core';
import classNames from 'classnames';
import { ReactComponent as SearchIcon } from 'img/icons/search.svg';
import { ReactComponent as CloseIcon } from 'img/icons/times.svg';
import sidebarMessages from 'common/messages/sidebar';
import useSidebarContext from 'common/hooks/context/useSidebarContext';
import TextField from 'common/components/TextField/TextField';

const useStyles = makeStyles((theme: Theme) => ({
  sidebarFilterContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '15px 0',
    backgroundColor: '#323232',
  },
  sidebarFilter: {
    '& .MuiInputBase-root': {
      backgroundColor: '#323232',
    },
    '& .MuiInputBase-input': {
      color: theme.palette.darkBackground?.light,
      backgroundColor: '#323232',
    },
    '& .Mui-focused .adornmentSearchIcon': {
      color: theme.palette.primary.main,
    },
  },
  btnSearch: {
    fontSize: '20px',
    padding: '6px',
    color: 'white',
    '&:hover': {
      backgroundColor: alpha(theme.palette.secondary.main, 0.7),
    },
  },
  adornmentSearchIcon: {
    fontSize: '19px',
    color: theme.palette.darkBackground?.light,
  },
  adornmentBtnClear: {
    cursor: 'pointer',
    '& svg': {
      fontSize: '16px',
      color: theme.palette.darkBackground?.light,
    },
    '& .MuiIconButton-root:hover': {
      backgroundColor: 'transparent',
      '& svg': {
        color: '#d7d7d7',
      },
    },
  },
}));

const SidebarFilter: FC = () => {
  const classes = useStyles();
  const intl = useIntl();
  const refAutoFocus = useRef<boolean>(false);
  const { search, setSearch, sidebarOpen, setSidebarOpen } = useSidebarContext();

  useEffect(() => {
    if (sidebarOpen && refAutoFocus.current) {
      refAutoFocus.current = false;
    }
  }, [sidebarOpen]);

  return (
    <div className={classes.sidebarFilterContainer}>
      {sidebarOpen ? (
        <TextField
          fullWidth
          name="sidebarFilter"
          size="small"
          variant="outlined"
          autoComplete="off"
          autoFocus={refAutoFocus.current}
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder={intl.formatMessage(sidebarMessages.findMenuItem)}
          className={classes.sidebarFilter}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgIcon
                  viewBox="0 0 24 24"
                  className={classNames('adornmentSearchIcon', classes.adornmentSearchIcon)}
                  component={SearchIcon}
                />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment className={classes.adornmentBtnClear} position="end">
                <IconButton onClick={() => setSearch('')} disableRipple>
                  <SvgIcon viewBox="0 0 24 24" component={CloseIcon} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      ) : (
        <IconButton
          className={classes.btnSearch}
          onClick={() => {
            refAutoFocus.current = true;
            setSidebarOpen(true);
          }}
          disableRipple
        >
          <SvgIcon viewBox="0 0 24 24" component={SearchIcon} />
        </IconButton>
      )}
    </div>
  );
};

export default SidebarFilter;
